import { animate, state, style, transition, trigger } from '@angular/animations';
import { DataSource } from '@angular/cdk/table';
import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { GalaxyDatePipe } from '@vendasta/galaxy/pipes';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import { Observable, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { BulkImportService } from '../../bulk-import/bulk-import.service';
import { FEATURE_BULK_ACTIONS_V2 } from '../../flags';
import { AddToListDialogComponent } from '../actions/add-to-list-dialog.component';
import {
  ActionListHistory,
  ActionListHistoryError,
  ActionListsHistoryStoreService,
  ActionListStatuses,
} from './action-lists-history.store.service';
import { ToggleEvent } from './columns/common';
import { Retry } from './columns/number-of-accounts';
import { HistoryItemsProgressReporter } from './history-items-reporter.service';

@Component({
  selector: 'app-action-list-history',
  templateUrl: './action-lists-history.component.html',
  styleUrls: ['../action-lists-details.component.scss', 'action-lists-history.component.scss'],
  animations: [
    trigger('errorExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', display: 'none' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  providers: [HistoryItemsProgressReporter],
})
export class ActionListsHistoryComponent implements OnInit, OnDestroy {
  @Input() actionListId: string;
  @Input() actionListName: string;
  public actionListStatuses = ActionListStatuses;
  DateFormat = DateFormat;

  historyDataSource: ActionListHistoryDataSource;
  readonly displayedColumns$: Observable<string[]>;
  readonly loading$: Observable<boolean>;
  readonly hasMore$: Observable<boolean>;
  expandedItemId: string;
  timezone: string;
  private subscriptions: Subscription[] = [];

  constructor(
    private actionListHistoryStoreService: ActionListsHistoryStoreService,
    private snackbarService: SnackbarService,
    private dialog: MatDialog,
    private readonly bulkImportService: BulkImportService,
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    @Inject(FEATURE_BULK_ACTIONS_V2)
    readonly isBulkActionsV2Enabled$: Observable<boolean>,
  ) {
    this.loading$ = this.actionListHistoryStoreService.loading$;
    this.hasMore$ = this.actionListHistoryStoreService.hasMore$;
    this.displayedColumns$ = this.determineColumns();
  }

  private determineColumns(): Observable<string[]> {
    return this.isBulkActionsV2Enabled$.pipe(
      map((v2Enabled) => {
        const baseColumns = ['title', 'number-of-accounts', 'performed-by', 'performed-date', 'actions'];
        if (v2Enabled) {
          const start = baseColumns.slice(0, baseColumns.indexOf('number-of-accounts'));
          const end = baseColumns.slice(baseColumns.indexOf('number-of-accounts'), baseColumns.length);
          return start.concat('status').concat(end);
        }
        return baseColumns;
      }),
    );
  }

  ngOnInit(): void {
    this.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.historyDataSource = new ActionListHistoryDataSource(this.actionListHistoryStoreService, this.actionListId);
  }

  retryListImport(event: Retry): void {
    const historyItem = event.historyItem;
    this.actionListHistoryStoreService.retryImport(historyItem.itemId);
    this.bulkImportService
      .retryListImport(historyItem.importListSessionId, this.actionListId, historyItem.itemId)
      .subscribe(
        (_) => {
          return;
        },
        (_) => this.actionListHistoryStoreService.markImportAsFailed(historyItem.itemId),
      );
  }

  loadNextPage(): void {
    this.actionListHistoryStoreService.loadMore();
  }

  toggleInlineErrors(event: ToggleEvent): void {
    if (event.showErrors) {
      this.expandedItemId = event.itemId;
    } else {
      this.expandedItemId = null;
    }
  }

  private generateDefaultListName(historyItem: ActionListHistory, historyError: ActionListHistoryError): string {
    const performedDate = new GalaxyDatePipe().transform(historyItem.performedDate, DateFormat.medium);
    return `${this.actionListName} (${historyError.reason}) ${performedDate}`;
  }

  openAddErrorAccountsToList(historyItem: ActionListHistory, historyError: ActionListHistoryError): void {
    this.dialog.closeAll();
    const dialogData = {
      title: `Add ${historyError.numberOfAccounts} matching Accounts to a list`,
      defaultListName: this.generateDefaultListName(historyItem, historyError),
    };
    const addToListDialog = this.dialog.open(AddToListDialogComponent, {
      width: '620px',
      data: dialogData,
    });
    this.subscriptions.push(
      addToListDialog.componentInstance.selectedActionList
        .pipe(
          switchMap((al) => {
            return this.actionListHistoryStoreService.addErrorAccountsToList(
              al.name,
              historyItem.itemId,
              historyError.reason,
            );
          }),
        )
        .subscribe(
          () => true,
          () => this.snackbarService.errorSnack('Failed to add accounts with error to the list. Please try again.'),
        ),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}

class ActionListHistoryDataSource extends DataSource<any> {
  public readonly history$: Observable<ActionListHistory[]>;
  public isLoading = true;
  public totalItemsText$: Observable<string>;
  constructor(
    private actionListsHistoryStoreService: ActionListsHistoryStoreService,
    private actionListId: string,
  ) {
    super();
    this.history$ = this.actionListsHistoryStoreService.history$;
    this.actionListsHistoryStoreService.loadHistory(this.actionListId);
    this.totalItemsText$ = this.history$.pipe(
      map((a) => {
        const totalItems = a.length;
        return totalItems === 1 ? `${totalItems} item` : `${totalItems} items`;
      }),
    );
  }

  connect(): Observable<ActionListHistory[]> {
    return this.history$;
  }

  disconnect(): void {
    return; // so lint is happy
  }
}
